import { Box, LinearProgress, Pagination, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useSearchParamsContext, useSearchQueryContext } from '../search.hooks';
import { useTranslation } from 'react-i18next';

type SearchDataCardGridProps = {
  emptyDataText?: string;
  CardComponent: React.ComponentType<any>;
  'data-testid'?: string;
};

const SearchDataCardGrid = ({
  emptyDataText,
  CardComponent,
  'data-testid': dataTestId,
}: SearchDataCardGridProps) => {
  const { t } = useTranslation();
  const { searchParams, setPaginationModel, setParamValue } =
    useSearchParamsContext();
  const {
    paging,
    searchResults,
    queryState: { isFetching },
  } = useSearchQueryContext();

  useEffect(() => {
    setPaginationModel({
      page: Number(searchParams.page.value),
      pageSize: Number(searchParams.count.value),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.page.value >= paging?.totalPages && paging?.total > 0) {
      setParamValue('page', paging.totalPages - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging?.totalPages]);

  const handleOnChange = (_e, value) => {
    setPaginationModel({
      page: value - 1,
      pageSize: Number(searchParams.count.value),
    });
  };

  return (
    <Box data-testid={dataTestId}>
      {isFetching && !searchResults && (
        <LinearProgress data-testid="IncCardLoader.LinearProgress" />
      )}
      {!isFetching && paging?.total === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="subtitle1" gutterBottom>
            {emptyDataText}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gap: 4, // Gap between cards
              gridTemplateColumns: {
                xs: '1fr', // 1 card for extra-small and small screens
                sm: 'repeat(2, 1fr)', // 2 cards for medium screens
                lg: 'repeat(3, 1fr)', // 3 cards for large screens
              },
            }}
          >
            {searchResults?.map((item, index) => (
              <CardComponent key={index} {...item} isLoading={isFetching} />
            ))}
            {searchResults?.length === 0 && (
              <Typography>
                {t(
                  'page.communications.emailTemplates.searchDataCardGrid.errors.noDataAlert',
                )}
              </Typography>
            )}
          </Box>
          {paging && (
            <Box textAlign="center" pt={3}>
              <Pagination
                page={Number(searchParams.page.value) + 1}
                count={paging.totalPages}
                onChange={handleOnChange}
                variant="outlined"
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchDataCardGrid;
